/**
 * What the month close needs, in one place for Hjem, Innboks and Timeliste (plan 0026 P1, "one
 * attention taxonomy"). Before this, Hjem counted the last ten days, Innboks the last five weeks
 * and Timeliste the month, each with its own words, so the three numbers never agreed.
 *
 * The window is the pay month, by the shift's start date in Oslo, and the previous month too:
 * every company approves the previous month in one batch around the 16th to 20th (production
 * copy, 2026-09-21: May on 19 Jun, June on 20 Jul, July on 18 Aug, August on 16 Sep), and a
 * few shifts stay behind after that batch, so "wait until it is clean" would pin it for ever.
 * The previous month is one line, the current month one line per kind. close.ts decides what
 * each shift is.
 */
import type { ApiShift } from '@/api/data';
import { T } from '@/i18n';
import { oslo } from '@/rules';
import { MONTHS } from '@/ui/format';
import { type Counts, countKinds } from './close';

export const monthOf = (d: string) => d.slice(0, 7);
export const prevMonth = (m: string) => {
  const [y, mo] = m.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, mo - 2, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};
export const lastDayOf = (m: string) => {
  const [y, mo] = m.split('-').map(Number) as [number, number];
  return `${m}-${String(new Date(Date.UTC(y, mo, 0)).getUTCDate()).padStart(2, '0')}`;
};
/** The dates to fetch for timeItems: the first of last month to the end of this one. */
export const closeWindow = (today: string) => ({
  from: `${prevMonth(monthOf(today))}-01`,
  to: lastDayOf(monthOf(today)),
});

export type Tone = 'bad' | 'warn' | 'info';
export interface TimeItem {
  key: string;
  /** Written for a person, with the count in it. */
  label: string;
  count: number;
  tone: Tone;
  to: string;
  note: string;
}

const plural = (n: number, one: string, many: string) => (n === 1 ? T(one, { n }) : T(many, { n }));
const monthName = (m: string) => {
  const s = MONTHS[Number(m.slice(5, 7)) - 1] ?? m;
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export function monthCounts(shifts: ApiShift[], month: string, now: number, tol = 15): Counts {
  return countKinds(
    shifts.filter((s) => monthOf(oslo(s.start_time)?.date ?? '') === month),
    now,
    tol,
  );
}

export function timeItems(shifts: ApiShift[], today: string, now: number, tol = 15): TimeItem[] {
  const cur = monthOf(today);
  const prev = prevMonth(cur);
  const link = (vis: string, m: string) => `/timeliste?vis=${vis}&mnd=${m}`;
  const out: TimeItem[] = [];

  const p = monthCounts(shifts, prev, now, tol);
  const left = p.action + p.ready;
  if (left)
    out.push({
      key: 'prev-month',
      label: `${monthName(prev)}: ${plural(left, '{n} vakt ikke godkjent', '{n} vakter ikke godkjent')}`,
      count: left,
      tone: p.missing + p.open ? 'bad' : 'warn',
      to: link(p.action ? 'handling' : 'klare', prev),
      note: T('Forrige måned. Bare godkjente timer blir betalt'),
    });

  const c = monthCounts(shifts, cur, now, tol);
  if (c.missing)
    out.push({
      key: 'missing',
      label: plural(c.missing, '{n} vakt uten stempling', '{n} vakter uten stempling'),
      count: c.missing,
      tone: 'bad',
      to: link('uten', cur),
      note: T('Timene finnes ikke i lønnsgrunnlaget'),
    });
  if (c.open)
    out.push({
      key: 'open',
      label: plural(c.open, '{n} åpen stempling', '{n} åpne stemplinger'),
      count: c.open,
      tone: 'bad',
      to: link('uten', cur),
      note: T('Vakten er over og klokken går fortsatt'),
    });
  if (c.deviation)
    out.push({
      key: 'deviation',
      label: plural(c.deviation, '{n} vakt med avvik fra plan', '{n} vakter med avvik fra plan'),
      count: c.deviation,
      tone: 'warn',
      to: link('avvik', cur),
      note: T('Må ses over før de godkjennes'),
    });
  if (c.ready)
    out.push({
      key: 'ready',
      label: plural(c.ready, '{n} vakt klar til godkjenning', '{n} vakter klare til godkjenning'),
      count: c.ready,
      tone: 'info',
      to: link('klare', cur),
      note: T('Godkjente timer er det som går til lønn'),
    });
  return out;
}
